import { useState, FormEvent } from 'react'
import { motion } from 'framer-motion'
import FadeIn from '@/components/animations/FadeIn'
import Input from '@/components/ui/Input'
import TextArea from '@/components/ui/TextArea'
import Button from '@/components/ui/Button'
import SocialLinks from '@/components/ui/SocialLinks'
import BlockchainProfiles from '@/components/ui/BlockchainProfiles'
import { getSiteSettings } from '@/lib/api'
import { SiteSettings } from '@/types'

interface FormState {
  name: string
  email: string
  subject: string
  message: string
}

interface FormErrors {
  name?: string
  email?: string
  subject?: string
  message?: string
}

const ContactPage = () => {
  const [formData, setFormData] = useState<FormState>({
    name: '',
    email: '',
    subject: '',
    message: '',
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<{
    success?: boolean
    message?: string
  }>({})

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }))
    }
  }

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address'
      }
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    setSubmitStatus({})

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (response.ok) {
        setSubmitStatus({
          success: true,
          message: data.message || 'Your message has been sent successfully!',
        })

        // Reset form on success
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
        })
      } else {
        setSubmitStatus({
          success: false,
          message: data.message || 'Failed to send message. Please try again.',
        })
      }
    } catch (error) {
      setSubmitStatus({
        success: false,
        message: 'An error occurred. Please try again later.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <FadeIn>
        <h1 className="text-4xl font-bold mb-8">Contact</h1>
      </FadeIn>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FadeIn delay={0.1}>
          <div>
            <h2 className="text-2xl font-semibold mb-4">Get in Touch</h2>
            <p className="mb-6">
              Interested in working together or have questions about my blockchain projects? Feel
              free to reach out using the form or through my social media channels.
            </p>

            <div className="mt-8">
              <h3 className="text-xl font-semibold mb-4">Connect</h3>
              <SocialLinks showLabels iconSize="md" className="mb-6" />

              <h3 className="text-xl font-semibold mb-4 mt-8">Blockchain Profiles</h3>
              <BlockchainProfiles />
            </div>
          </div>
        </FadeIn>

        <FadeIn delay={0.2}>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-semibold mb-4">Send a Message</h2>

            {submitStatus.success ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-green-100 dark:bg-green-900 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 p-4 rounded-md mb-4"
              >
                <p>{submitStatus.message}</p>
              </motion.div>
            ) : null}

            {submitStatus.success === false ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-100 dark:bg-red-900 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 p-4 rounded-md mb-4"
              >
                <p>{submitStatus.message}</p>
              </motion.div>
            ) : null}

            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Name"
                id="name"
                name="name"
                type="text"
                placeholder="Your name"
                value={formData.name}
                onChange={handleChange}
                error={errors.name}
                required
                helpText="Enter your full name"
              />

              <Input
                label="Email"
                id="email"
                name="email"
                type="email"
                placeholder="your.email@example.com"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
                required
                helpText="We'll use this to respond to your message"
              />

              <Input
                label="Subject"
                id="subject"
                name="subject"
                type="text"
                placeholder="What is this regarding?"
                value={formData.subject}
                onChange={handleChange}
                error={errors.subject}
                helpText="Brief description of your inquiry"
              />

              <TextArea
                label="Message"
                id="message"
                name="message"
                placeholder="Your message here..."
                value={formData.message}
                onChange={handleChange}
                error={errors.message}
                required
                helpText="Please provide details about your inquiry"
              />

              <div className="mt-6">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </div>
            </form>
          </div>
        </FadeIn>
      </div>
    </div>
  )
}

export default ContactPage
